import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useApp } from '../lib/app';
import { supabase } from '../lib/supabase';
import { useRows } from '../lib/useRows';
import { STAGES, fmtDate, stageLabel } from '../lib/format';
import { Badge, Button, Card, Empty, PageHeader } from '../ui/kit';
import { RecordDrawer, type FieldSpec } from '../ui/RecordDrawer';

type Row = Record<string, unknown> & {
  id: string;
  name: string;
  short_code: string | null;
  aliases: string[];
  stage: string;
  project_id: string | null;
  pc_date: string | null;
  revised_pc_date: string | null;
  drive_folder_url: string | null;
  notes: string | null;
  deleted_at: string | null;
};

const NONE = '00000000-0000-0000-0000-000000000000';

export default function Buildings() {
  const { siteId, site, can } = useApp();
  const [params, setParams] = useSearchParams();
  const [showDeleted, setShowDeleted] = useState(false);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState<Row | null | 'new'>(null);

  const list = useRows<Row>(
    () => supabase.from('buildings').select('*').eq('site_id', siteId ?? NONE).order('sort').order('name'),
    [siteId],
    ['buildings'],
  );
  const projects = useRows<{ id: string; name: string }>(
    () => supabase.from('projects').select('id, name').eq('site_id', siteId ?? NONE).is('deleted_at', null).order('sort'),
    [siteId],
    ['projects'],
  );
  const contracts = useRows<{ building_id: string | null }>(
    () => supabase.from('contracts').select('building_id').eq('site_id', siteId ?? NONE).is('deleted_at', null),
    [siteId],
    ['contracts'],
  );

  useEffect(() => {
    const id = params.get('open');
    if (id && list.rows.length) {
      const r = list.rows.find((x) => x.id === id);
      if (r) setOpen(r);
    }
  }, [params, list.rows]);

  const fields = useMemo<FieldSpec[]>(
    () => [
      { key: 'name', label: 'Name', required: true },
      { key: 'short_code', label: 'Short code', hint: 'e.g. TH, SUN, SV' },
      { key: 'aliases', label: 'Other names', type: 'list', hint: 'Comma separated — used to match old Smartsheet names' },
      { key: 'project_id', label: 'Project', type: 'select', options: projects.rows.map((p) => ({ value: p.id, label: p.name })) },
      { key: 'stage', label: 'Stage', type: 'select', options: STAGES, required: true },
      { key: 'pc_date', label: 'Practical completion date', type: 'date' },
      { key: 'revised_pc_date', label: 'Revised PC date (after EOTs)', type: 'date' },
      { key: 'drive_folder_url', label: 'Google Drive folder', type: 'url', hint: "Where the client's copies of this building's files live" },
      { key: 'notes', label: 'Notes', type: 'textarea' },
    ],
    [projects.rows],
  );

  const shown = list.rows.filter(
    (r) =>
      (showDeleted || !r.deleted_at) &&
      (!q || [r.name, r.short_code, ...(r.aliases ?? [])].join(' ').toLowerCase().includes(q.toLowerCase())),
  );
  const countFor = (id: string) => contracts.rows.filter((c) => c.building_id === id).length;
  const editable = can('buildings', 4);

  return (
    <>
      <PageHeader
        title="Buildings & projects"
        sub={site?.name}
        actions={editable ? <Button kind="primary" onClick={() => setOpen('new')}>+ Add building</Button> : null}
      />
      <div className="toolbar">
        <input type="search" placeholder="Find a building" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Find a building" />
        <label className="check small">
          <input type="checkbox" checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)} /> Show deleted
        </label>
      </div>
      <Card pad={false}>
        {list.error ? <div className="error-box">{list.error}</div> : null}
        {list.loading ? (
          <Empty>Loading…</Empty>
        ) : shown.length === 0 ? (
          <Empty>No buildings found.</Empty>
        ) : (
          <div className="table-wrap">
            <table className="t">
              <thead>
                <tr>
                  <th>Building</th>
                  <th>Code</th>
                  <th>Stage</th>
                  <th>PC date</th>
                  <th>Revised PC</th>
                  <th>Contracts</th>
                  <th>Drive</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => (
                  <tr key={r.id} className={`click ${r.deleted_at ? 'deleted' : ''}`} onClick={() => setOpen(r)}>
                    <td>
                      <strong>{r.name}</strong>
                      {r.aliases?.length ? <div className="muted small">also: {r.aliases.join(', ')}</div> : null}
                    </td>
                    <td>{r.short_code ?? '—'}</td>
                    <td>
                      <Badge tone={r.stage === 'construction' ? 'orange' : r.stage === 'complete' ? 'good' : 'neutral'}>{stageLabel(r.stage)}</Badge>
                    </td>
                    <td>{fmtDate(r.pc_date)}</td>
                    <td>{fmtDate(r.revised_pc_date)}</td>
                    <td>{countFor(r.id)}</td>
                    <td>
                      {r.drive_folder_url ? (
                        <a href={r.drive_folder_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                          Open
                        </a>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <RecordDrawer
        open={open !== null}
        onClose={() => {
          setOpen(null);
          if (params.get('open')) setParams({}, { replace: true });
        }}
        title={open === 'new' ? 'Add building' : open ? open.name : ''}
        table="buildings"
        fields={fields}
        record={open === 'new' ? null : open}
        defaults={{ site_id: siteId, stage: 'construction', project_id: projects.rows[0]?.id ?? null }}
        canEdit={editable}
        onSaved={() => void list.reload()}
        label={(r) => `building ${String(r.name ?? '')}`}
      />
    </>
  );
}
